import {BehaviorSubject, merge, Subject} from 'rxjs';
import {filter, map, mergeAll, scan, withLatestFrom} from 'rxjs/operators';
import {numUtil} from '@/helper/num';
import {contracts$} from '@/config';
import {aopMsg$, aopMsgV2$} from '@/raw/aop';
import {positions$, positionsV2$} from '@/positions/positions-cache';
import {convertPositionV2} from '@/positions/positions-cache';
import type {ws, ReduceAction} from '@/types';

type ContractPositionRaw = ws.ContractPositionRaw;

const snapshotPosition$ = merge(positions$, positionsV2$);
const aopMsgV2Tmp$ = aopMsgV2$.pipe(
  filter((o) => o.type === 'incremental'),
  map((o) => {
    const events = convertEvent(o.events);
    const positions = convertPositionV2(o.positions);
    return {...o, events, positions};
  })
);

const incremental$ = merge(aopMsg$.pipe(filter((o) => o.type === 'incremental')), aopMsgV2Tmp$);
const incrementalPosition$ = incremental$.pipe(
  map(({events, positions}) => {
    if (positions.length < 1) {
      return null;
    }
    return positions.map((position) => {
      const event = events.find(({symbol, posSide}) => position.symbol === symbol && position.posSide === posSide);
      return {...position, event};
    });
  }),
  filter(Boolean),
  mergeAll()
);

const snapshotAction$ = snapshotPosition$.pipe(
  map((positions) => {
    return function (cache: Map<string, ContractPositionRaw>) {
      positions.forEach((o) => {
        const {symbol, posSide} = o;
        const key = symbol + (posSide ? posSide : '');
        cache && cache.has(key) ? cache.set(key, {...o, ...cache.get(key)}) : cache.set(key, o);
      });
      return cache;
    };
  })
);

const updateAction$ = new Subject<ReduceAction<Map<string, ContractPositionRaw>>>();
// TODO: position cache
const positionCache$ = new BehaviorSubject(new Map<string, ContractPositionRaw>());
const notification$ = new Subject();

export const positionNotification$ = notification$.asObservable();

merge(snapshotAction$, updateAction$)
  .pipe(scan((p, c) => c(p), new Map<string, ContractPositionRaw>()))
  .subscribe((cache) => positionCache$.next(cache));

incrementalPosition$.pipe(withLatestFrom(contracts$)).subscribe(([position, contracts]) => {
  // @ts-ignore
  const contract = contracts.find((o) => o.symbol === position.symbol);
  if (!contract) {
    return;
  }

  const cache = positionCache$.getValue();
  const notification = convertPositionNotification(position, contract, cache);
  if (notification) {
    notification$.next(notification);
  }
  updateAction$.next(convertUpdateAction(position));
});

function convertPositionNotification(position, contract, cache) {
  const {event} = position;
  if (!event) {
    return null;
  }

  const {symbolV2, settleCurrencyDisplay, valuePrecision, valueScale, ratioScale} = contract;

  const Symbol = symbolV2;
  const SettleCurrency = settleCurrencyDisplay;

  if (event.code !== 0) {
    return {
      ID: 'Error',
      Code: event.code,
    };
  }
  // 设置杠杆
  if (event.action === 'SetLeverage') {
    const {symbol, posSide} = position;
    const key = symbol + (posSide ? posSide : 'Merged');
    const oldPosition = cache.get(key);
    if (oldPosition.leverageEr === position.leverageEr) {
      return null;
    }
    return {
      ID: 'LeverageModified',
      Symbol,
      SettleCurrency,
      OldIsIsolated: oldPosition.leverageEr > 0,
      OldLeverage: convertLeverage(oldPosition.leverageEr, ratioScale),
      Leverage: convertLeverage(position.leverageEr, ratioScale),
      IsIsolated: position.leverageEr > 0,
    };
  }

  // 修改保证金
  if (event.action === 'AssignPosBalance') {
    const {execValueEv} = event;
    return {
      ID: execValueEv > 0 ? 'MarginAdded' : 'MarginRemoved',
      Symbol,
      SettleCurrency,
      Amount: numUtil.floor(Math.abs(execValueEv), valuePrecision, valueScale),
      Currency: settleCurrencyDisplay,
    };
  }

  // 修改风险限额
  if (event.action === 'SetRiskLimit') {
    return {
      ID: 'RiskLimitModified',
      Symbol,
      SettleCurrency,
      RiskLimit: numUtil.floor(position.riskLimitEv, 0, valueScale),
    };
  }

  // 仓位强平
  if (event.action === 'LiqRequest') {
    return {
      ID: 'PositionLiquidated',
      Symbol,
      SettleCurrency,
      Side: event.side === 'Buy' ? 'Sell' : 'Buy',
      Size: event.orderQty,
    };
  }

  // 自动减仓
  if (event.action === 'ADLResult') {
    return {
      ID: 'AutoDeleveraged',
      Symbol,
      SettleCurrency,
      Side: position.side,
      Size: position.size,
    };
  }

  return null;
}

function convertUpdateAction(position) {
  return function (cache) {
    const {symbol, posSide} = position;
    const key = symbol + (posSide ? posSide : '');
    cache && cache.has(key) ? cache.set(key, {...cache.get(key), ...position}) : cache.set(key, position);
    return cache;
  };
}

function convertLeverage(leverageEr, ratioScale) {
  return numUtil.trimEnd0(numUtil.ceil(Math.abs(leverageEr), 2, ratioScale));
}

function convertEvent(events) {
  return events.map((o) => ({...o, execValueEv: Number(o.execValueRv)}));
}
